import React, { useState, forwardRef, useRef, ClipboardEvent, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { IInvalidProps } from '../../fundamentals/actions';
import { PX_TO_REM, PxToRem, SIZES } from '../../fundamentals/sizes/sizes';
import { IGapSizeProps, IGutterAsMarginProps, useGapCss, useGutterAsMarginCss } from '../../fundamentals/spaces/space';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { ErrorText } from '../ErrorText/ErrorText';
import { HelpText } from '../HelpText/HelpText';
import { Label } from '../Label/Label';
import { DEFAULT_INPUT_SIZE, IInputSizeProps, InputSize, ITextFieldProps, TextField } from '../TextField/TextField';
import { useInputWidthCss } from '../TextField/textfieldStyles';
import { BaseDiv } from '../../common/BaseElements';
import { EORA_NUMBERS } from '../../fundamentals/numbers/eoraNumbers';

export enum VerificationCodeLength {
  Four = 4,
  Six = 6
}

export interface IVerificationCodeProps extends Pick<ITextFieldProps, 'placeholder'>, IGutterAsMarginProps, IInputSizeProps, IInvalidProps {
  codeLength?: VerificationCodeLength;
  invalidIfEmpty?: boolean;
  label?: string;
  errorText?: string;
  helpText?: string;
  required?: boolean;
  autoFocus?: boolean;
  verificationCode?: string;
  onVerificationCodeProvided?: (verificationCode: string) => void;
}

interface ISingleDigitTextFieldProps extends ITextFieldProps {
  digit?: string;
  onDigitChange: (digit?: string, specialKey?: SpecialKeys) => void;
  onDigitsPasted: (pasted: string) => void;
}

enum SpecialKeys {
  BACKSPACE = 'Backspace',
  DELETE = 'Delete'
}

const WIDTH: Record<InputSize, PxToRem> = {
  [SIZES.SMALL]: PX_TO_REM[EORA_NUMBERS.CORE.WIDTHS.SINGLE_DIGIT_INPUT_WIDTHS.SIZE_INPUT_SINGLEDIGIT_WIDTH_SMALL],
  [SIZES.MEDIUM]: PX_TO_REM[EORA_NUMBERS.CORE.WIDTHS.SINGLE_DIGIT_INPUT_WIDTHS.SIZE_INPUT_SINGLEDIGIT_WIDTH_MEDIUM],
  [SIZES.LARGE]: PX_TO_REM[EORA_NUMBERS.CORE.WIDTHS.SINGLE_DIGIT_INPUT_WIDTHS.SIZE_INPUT_SINGLEDIGIT_WIDTH_LARGE]
};

const SINGLE_DIGIT_REGEX = /^\d$/;
const NON_DIGIT_REGEX = /\D/g;

const sanitiseToDigits = (input: string) => input.replaceAll(NON_DIGIT_REGEX, '');
const safeIndex = (index: number, max: number) => Math.min(max - 1, Math.max(0, index));
const codeToDigits = (codeLength: VerificationCodeLength, input?: string): (string | undefined)[] => {
  const provided = input ? [...sanitiseToDigits(input)].slice(0, codeLength) : [];
  const buffer = Array.from({ length: codeLength - provided.length }) as undefined[];
  return [...provided, ...buffer];
};

const VerificationCodeContainer = styled(BaseDiv)<IVerificationCodeProps & IGapSizeProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  ${useGutterAsMarginCss()};
  ${useGapCss()};
`;
const CodeContainer = styled(BaseDiv)<IVerificationCodeProps & IGapSizeProps>`
  display: flex;
  align-items: flex-start;
  ${useGapCss()}
`;
const VerificationCodeInput = styled(TextField)`
  ${({ inputSize }) => css`
    ${useInputWidthCss(WIDTH[inputSize!])};
  `};
`;

const SingleDigitTextField = forwardRef<HTMLInputElement, ISingleDigitTextFieldProps>(({ onDigitChange, autoFocus, onDigitsPasted, digit, ...rest }, ref) => {
  const [value, setValue] = useState(digit ?? '');
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === SpecialKeys.BACKSPACE || e.key === SpecialKeys.DELETE) {
      setValue('');
      onDigitChange(undefined, e.key);
    } else if (SINGLE_DIGIT_REGEX.test(e.key)) {
      setValue(e.key);
      onDigitChange(e.key);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    let pastedValue = e.clipboardData.getData('text');
    pastedValue = sanitiseToDigits(pastedValue);
    if (pastedValue.length === 0) return;
    onDigitsPasted?.(pastedValue);
  };

  useEffect(() => {
    setValue(digit ?? '');
  }, [digit]);

  return (
    <VerificationCodeInput
      autoFocus={autoFocus}
      {...rest}
      value={value}
      ref={ref}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      maxLength={1}
      textAlign='center'
      onChange={(e) => rest.onChange?.(e)}
      type='number'
      // Note: ask Bosko about these config before doing PR
      // This hides the blinking caret when commented out
      // readOnly
    />
  );
});

export const VerificationCode: React.FC<IVerificationCodeProps> = ({
  verificationCode,
  codeLength = VerificationCodeLength.Four,
  inputSize = DEFAULT_INPUT_SIZE,
  onVerificationCodeProvided,
  placeholder,
  invalid,
  invalidIfEmpty,
  label,
  errorText,
  required,
  helpText,
  autoFocus,
  ...rest
}) => {
  const { generateTestId } = useTestId(rest, 'VerificationCode');
  const [code, setCode] = useState<(string | undefined)[]>(codeToDigits(codeLength, verificationCode));

  const inputRefs = useRef<HTMLInputElement[]>([]);

  const moveFocus = (from: number, to: number) => {
    inputRefs.current[safeIndex(from, codeLength)]?.blur();
    inputRefs.current[safeIndex(to, codeLength)]?.focus();
  };
  const moveBack = (index: number) => moveFocus(index, index - 1);
  const moveForward = (index: number) => moveFocus(index, index + 1);

  const handleDigitChange = (index: number, digit?: string, specialKey?: SpecialKeys) => {
    const isEmpty = !code[index];
    code[index] = digit;
    setCode([...code]);
    if (digit && index < codeLength - 1) moveForward(index);
    if (isEmpty && !digit && specialKey === SpecialKeys.BACKSPACE) moveBack(index);
    if (index === codeLength - 1 && code.every((character) => !!character)) onVerificationCodeProvided?.(code.join(''));
  };

  const handleDigitsPasted = (pasted: string) => {
    const pastedDigits = [...pasted].slice(0, codeLength);
    const digits = [...Array.from({ length: codeLength }).keys()].map((key) => pastedDigits[key]);
    setCode(digits);
    inputRefs.current.forEach((inputRef) => inputRef?.blur());
    if (digits.every((digit) => !!digit)) onVerificationCodeProvided?.(digits.join(''));
  };

  return (
    <VerificationCodeContainer gap={'medium'} {...generateTestId()} {...rest}>
      {label && (
        <Label {...generateTestId('Label')} invalid={invalid || (invalidIfEmpty && !code.every((digit) => !!digit))} required={required}>
          {label}
        </Label>
      )}
      {helpText && <HelpText {...generateTestId('Help')}>{helpText}</HelpText>}
      {errorText && <ErrorText {...generateTestId('Error')}>{errorText}</ErrorText>}
      <CodeContainer gap={'medium'}>
        {code.map((digit, index) => (
          <SingleDigitTextField
            {...generateTestId(`Input.${index}`)}
            {...rest}
            invalid={invalid || (invalidIfEmpty && !digit)}
            placeholder={placeholder?.[index]}
            ref={(element) => {
              if (element) inputRefs.current.push(element);
            }}
            inputSize={inputSize}
            key={index}
            digit={digit}
            onDigitChange={(newDigit, specialKey) => handleDigitChange(index, newDigit, specialKey)}
            onDigitsPasted={handleDigitsPasted}
            {...(index === 0 && autoFocus ? { autoFocus: true } : {})}
          />
        ))}
      </CodeContainer>
    </VerificationCodeContainer>
  );
};
